import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Paperclip, X, Loader2, ArrowRight, Linkedin } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CONTACT } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { name: "name", label: "Full Name", type: "text", required: true, placeholder: "Your name" },
  { name: "company", label: "Company", type: "text", required: false, placeholder: "Organisation" },
  { name: "email", label: "Email", type: "email", required: true, placeholder: "you@company.com" },
  { name: "phone", label: "Phone", type: "tel", required: false, placeholder: "+91 ..." },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "" });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("attachment", file);
      await axios.post(`${API}/contact`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Message sent. Our team will be in touch shortly.");
      setForm({ name: "", company: "", email: "", phone: "", message: "" });
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        index="/ 06"
        overline="Contact"
        title={<>Let&apos;s engineer<br />what&apos;s next.</>}
        subtitle="Share your requirement, specifications or drawings — our team responds to every serious enquiry."
      />

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="space-y-10">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
                  {FIELDS.map((f) => (
                    <div key={f.name} className="relative">
                      <label className="font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">
                        {f.label} {f.required && <span className="text-[hsl(var(--signal))]">*</span>}
                      </label>
                      <input
                        data-testid={`contact-${f.name}`}
                        type={f.type}
                        required={f.required}
                        placeholder={f.placeholder}
                        value={form[f.name]}
                        onChange={update(f.name)}
                        className="mt-3 w-full bg-transparent border-b border-border pb-3 text-lg focus:border-foreground focus:outline-none transition-colors placeholder:text-muted-foreground/50"
                      />
                    </div>
                  ))}
                </div>

                <div>
                  <label className="font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground">
                    Message <span className="text-[hsl(var(--signal))]">*</span>
                  </label>
                  <textarea
                    data-testid="contact-message"
                    required
                    rows={4}
                    placeholder="Tell us about your project or requirement..."
                    value={form.message}
                    onChange={update("message")}
                    className="mt-3 w-full bg-transparent border-b border-border pb-3 text-lg focus:border-foreground focus:outline-none transition-colors resize-none placeholder:text-muted-foreground/50"
                  />
                </div>

                {/* File upload */}
                <div>
                  <input
                    ref={fileRef}
                    type="file"
                    data-testid="contact-file"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                  {!file ? (
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      data-testid="contact-file-trigger"
                      className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors border border-dashed border-border px-5 py-3"
                    >
                      <Paperclip className="h-4 w-4" /> Attach file (optional)
                    </button>
                  ) : (
                    <div className="inline-flex items-center gap-3 border border-border px-5 py-3">
                      <Paperclip className="h-4 w-4 text-[hsl(var(--signal))]" />
                      <span className="text-sm max-w-[220px] truncate">{file.name}</span>
                      <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} data-testid="contact-file-remove">
                        <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                      </button>
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-button"
                  className="group inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-[13px] font-medium uppercase tracking-widest hover:bg-[hsl(var(--signal))] transition-colors disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />}
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </Reveal>
          </div>

          {/* Contact details */}
          <div className="lg:col-span-5 lg:border-l lg:border-border lg:pl-16">
            <Reveal delay={0.1}>
              <div className="space-y-10">
                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground mb-4">Direct</p>
                  <p className="text-lg font-medium mb-3">{CONTACT.contactName}</p>
                  <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 text-lg mb-3 hover:text-[hsl(var(--signal))] transition-colors">
                    <Phone className="h-4 w-4 text-muted-foreground" /> {CONTACT.phone}
                  </a>
                  <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-lg mb-3 hover:text-[hsl(var(--signal))] transition-colors">
                    <Mail className="h-4 w-4 text-muted-foreground" /> {CONTACT.email}
                  </a>
                  <a href={CONTACT.linkedin} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-lg hover:text-[hsl(var(--signal))] transition-colors">
                    <Linkedin className="h-4 w-4 text-muted-foreground" /> LinkedIn
                  </a>
                </div>

                <div>
                  <p className="font-mono-tech text-[10px] uppercase tracking-widest text-muted-foreground mb-5">Office</p>
                  <div className="flex gap-4 py-5 border-t border-border">
                    <MapPin className="h-4 w-4 text-[hsl(var(--signal))] mt-1 shrink-0" />
                    <div>
                      <p className="font-medium">{CONTACT.office.label}</p>
                      <p className="text-sm text-muted-foreground">{CONTACT.office.line}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
